// ─────────────────────────────────────────────
//  FundamentalTab.js — Tab 1: Fundamental Analysis
// ─────────────────────────────────────────────
import React, { useState, useEffect } from "react";
import { fetchOverview } from "../../config/api";
import "./FundamentalTab.css";

function fmtPrice(v) {
  if (!v) return "—";
  return "₹" + Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 });
}
function fmtCr(v) {
  if (!v) return "—";
  const n = Number(v);
  if (n >= 1e12) return "₹" + (n / 1e12).toFixed(2) + " L Cr";
  if (n >= 1e7)  return "₹" + (n / 1e7).toFixed(2)  + " Cr";
  if (n >= 1e5)  return "₹" + (n / 1e5).toFixed(2)  + " L";
  return "₹" + n.toLocaleString("en-IN");
}

export default function FundamentalTab({ symbol, exchange }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true); setError(null);
      try {
        const d = await fetchOverview(symbol, exchange);
        if (!cancelled) setData(d);
      } catch (e) {
        if (!cancelled) setError("Failed to load fundamentals.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [symbol, exchange]);

  if (loading) return <FundSkeleton />;
  if (error)   return <div className="ft-error">⚠ {error}</div>;
  if (!data)   return null;

  return (
    <div className="ft-wrap">

      {/* ── About ── */}
      {data.about && (
        <div className="ft-about-card">
          <div className="ft-section-label">About the Company</div>
          <p className="ft-about-text">{data.about}</p>
          <div className="ft-about-meta">
            {data.company_website && (
              <a className="ft-website" href={data.company_website} target="_blank" rel="noreferrer">
                🌐 {data.company_website}
              </a>
            )}
            {data.sector   && <span className="ft-badge">{data.sector}</span>}
            {data.industry && <span className="ft-badge muted">{data.industry}</span>}
          </div>
        </div>
      )}

      {/* ── Valuation Metrics ── */}
      <div className="ft-section-label">Valuation & Price</div>
      <div className="ft-metrics-grid">
        <MetricCard label="Market Cap"       value={fmtCr(data.company_market_cap)} />
        <MetricCard label="Current Price"    value={fmtPrice(data.price)} />
        <MetricCard label="P/E Ratio (TTM)"  value={data.company_pe_ratio ? Number(data.company_pe_ratio).toFixed(2) : "—"} highlight={peHighlight(data.company_pe_ratio)} />
        <MetricCard label="P/B Ratio"        value={data.company_pb_ratio ? Number(data.company_pb_ratio).toFixed(2) : "—"} />
        <MetricCard label="EPS (TTM)"        value={data.company_eps ? fmtPrice(data.company_eps) : "—"} />
        <MetricCard label="Book Value"       value={data.book_value ? fmtPrice(data.book_value) : "—"} />
        <MetricCard label="Dividend Yield"   value={data.company_dividend_yield ? `${data.company_dividend_yield}%` : "—"} />
        <MetricCard label="52W High"         value={fmtPrice(data.year_high)} color="green" />
        <MetricCard label="52W Low"          value={fmtPrice(data.year_low)}  color="red"   />
        <MetricCard label="Open"             value={fmtPrice(data.open)} />
        <MetricCard label="Prev Close"       value={fmtPrice(data.previous_close)} />
        <MetricCard label="Day Change"
          value={data.change ? `${data.change >= 0 ? "+" : ""}${Number(data.change).toFixed(2)} (${data.change_percent >= 0 ? "+" : ""}${Number(data.change_percent).toFixed(2)}%)` : "—"}
          color={parseFloat(data.change) >= 0 ? "green" : "red"} />
      </div>

      {/* ── 52-week position bar ── */}
      {data.year_high && data.year_low && data.price && (
        <div className="ft-range-card">
          <div className="ft-section-label" style={{ marginBottom: "0.75rem" }}>52-Week Position</div>
          <div className="ft-range-wrap">
            <span className="ft-range-label red">{fmtPrice(data.year_low)}</span>
            <div className="ft-range-track">
              <div
                className="ft-range-fill"
                style={{
                  width: `${Math.min(100, Math.max(0,
                    ((data.price - data.year_low) / (data.year_high - data.year_low)) * 100
                  ))}%`
                }}
              />
              <div
                className="ft-range-dot"
                style={{
                  left: `${Math.min(100, Math.max(0,
                    ((data.price - data.year_low) / (data.year_high - data.year_low)) * 100
                  ))}%`
                }}
              />
            </div>
            <span className="ft-range-label green">{fmtPrice(data.year_high)}</span>
          </div>
          <div className="ft-range-pct">
            {(((data.price - data.year_low) / (data.year_high - data.year_low)) * 100).toFixed(1)}% from 52W Low
          </div>
        </div>
      )}

      {/* ── Company Info ── */}
      <div className="ft-section-label">Company Information</div>
      <div className="ft-info-grid">
        <InfoRow label="Sector"      value={data.sector   || "—"} />
        <InfoRow label="Industry"    value={data.industry || "—"} />
        <InfoRow label="Exchange"    value={exchange} />
        <InfoRow label="Employees"   value={data.company_employees ? Number(data.company_employees).toLocaleString("en-IN") : "—"} />
        <InfoRow label="Currency"    value={data.currency || "INR"} />
        <InfoRow label="Last Update" value={data.last_update_utc ? new Date(data.last_update_utc).toLocaleString("en-IN") : "—"} />
      </div>

    </div>
  );
}

function MetricCard({ label, value, color, highlight }) {
  return (
    <div className={`ft-metric-card ${highlight || ""}`}>
      <span className="ft-metric-label">{label}</span>
      <span className={`ft-metric-value ${color ? `color-${color}` : ""}`}>{value}</span>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="ft-info-row">
      <span className="ft-info-label">{label}</span>
      <span className="ft-info-value">{value}</span>
    </div>
  );
}

function FundSkeleton() {
  return (
    <div className="ft-wrap">
      <div className="ft-skel-block" style={{ height: 80, marginBottom: "1rem" }} />
      <div className="ft-metrics-grid">
        {Array.from({ length: 12 }).map((_, i) => (
          <div key={i} className="ft-metric-card">
            <div className="ft-skel-line w50" />
            <div className="ft-skel-line w70" />
          </div>
        ))}
      </div>
    </div>
  );
}

function peHighlight(pe) {
  const v = parseFloat(pe);
  if (!v) return "";
  if (v < 15)  return "highlight-green";
  if (v < 25)  return "highlight-yellow";
  return "highlight-red";
}